import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Robot, X, Trash, Spinner, FilePdf, FileXls,
  ArrowRight, CaretDown, Microphone, MicrophoneSlash, Stop,
  NavigationArrow, Table as TableIcon, PlusCircle,
  ChatCircleText, Warning, PaperPlaneTilt,
  Package, Tag, ArrowsClockwise, Cube, User, Command,
  Copy, Check
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGERS = {
  '@': { type: 'customer', icon: User, color: '#7d64ff', label: 'Customers', hint: 'Search customers...' },
  '#': { type: 'order', icon: Package, color: '#f6a609', label: 'Orders', hint: 'Search orders...' },
  '$': { type: 'product', icon: Cube, color: '#28cd8c', label: 'Products', hint: 'Search products...' },
  '!': { type: 'return', icon: ArrowsClockwise, color: '#ea4b5a', label: 'Returns', hint: 'Search returns...' },
  '%': { type: 'category', icon: Tag, color: '#ff7d00', label: 'Categories', hint: 'Search categories...' },
  '&': { type: 'seller', icon: User, color: '#3b82f6', label: 'Sellers', hint: 'Search sellers...' },
};

const STARTERS = [
  { text: 'Show my order summary', icon: Package },
  { text: 'Which products have low stock?', icon: Warning },
  { text: 'Top 10 selling products', icon: Cube },
  { text: "What's my return rate?", icon: ArrowsClockwise },
  { text: 'Revenue overview', icon: Tag },
  { text: 'List all cancelled orders', icon: Package },
];

/* ── Export Dropdown ─── */
function ExportDropdown({ data, text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14); doc.setTextColor(125, 100, 255);
    doc.text('Vinculum Report', 14, 18);
    doc.setFontSize(8); doc.setTextColor(156, 157, 183);
    doc.text(new Date().toLocaleString(), 14, 24);
    if (data?.columns?.length && data?.rows?.length) {
      doc.autoTable({ head: [data.columns], body: data.rows, startY: 30, styles: { fontSize: 7.5 }, headStyles: { fillColor: [125, 100, 255] }, alternateRowStyles: { fillColor: [248, 247, 255] } });
    } else if (text) {
      doc.setFontSize(10); doc.setTextColor(29, 29, 31);
      doc.text(doc.splitTextToSize(text, 180), 14, 30);
    }
    doc.save(`vinculum_report_${Date.now()}.pdf`);
    toast.success('PDF exported'); setOpen(false);
  };

  const exportXls = () => {
    if (!data?.columns?.length || !data?.rows?.length) return;
    const ws = XLSX.utils.aoa_to_sheet([data.columns, ...data.rows]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `vinculum_report_${Date.now()}.xlsx`);
    toast.success('Excel exported'); setOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-[#f5f5f7] border border-[#e5e5ea] rounded-lg hover:border-[#7d64ff] text-[#5a5c87] hover:text-[#7d64ff] transition-colors" data-testid="export-dropdown-btn">
        Export <CaretDown size={10} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-[#e5e5ea] rounded-lg shadow-lg overflow-hidden z-50 min-w-[120px]">
          <button onClick={exportPdf} className="flex items-center gap-2 w-full px-3 py-2 text-[12px] text-[#ea4b5a] hover:bg-[#fef2f2] transition-colors" data-testid="export-pdf"><FilePdf size={14} weight="fill" /> PDF</button>
          {data?.columns?.length > 0 && <button onClick={exportXls} className="flex items-center gap-2 w-full px-3 py-2 text-[12px] text-[#28cd8c] hover:bg-[#f0fdf4] transition-colors" data-testid="export-xls"><FileXls size={14} weight="fill" /> Excel</button>}
        </div>
      )}
    </div>
  );
}

/* ── Copy Button ─── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded hover:bg-[#f5f5f7] text-[#9c9db7] hover:text-[#7d64ff] transition-colors" title="Copy">
      {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
    </button>
  );
}

/* ── AI Response Display ─── */
function AIResponseDisplay({ response, onNavigate }) {
  const action = response?.action;
  const message = response?.message || '';
  const data = response?.data;

  if (action === 'NAVIGATE') {
    return (
      <div className="space-y-2.5">
        <p className="text-[13px] text-[#1d1d1f] leading-relaxed">{message}</p>
        {response.navigation && (
          <button onClick={() => onNavigate(response.navigation)} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12px] font-semibold bg-[#7d64ff] text-white rounded-lg hover:bg-[#6a54e0] transition-colors" data-testid="ai-navigate-btn">
            <NavigationArrow size={13} weight="fill" /> Go to page <ArrowRight size={11} />
          </button>
        )}
      </div>
    );
  }

  if (action === 'CREATE') {
    const cd = data?.data || data || response.create_data;
    return (
      <div className="space-y-2.5">
        <div className="flex items-start gap-2"><PlusCircle size={15} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" /><p className="text-[13px] text-[#1d1d1f] leading-relaxed">{message}</p></div>
        {cd && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Created</p>
            <pre className="text-[11px] text-[#1d1d1f] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(cd, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  }

  if ((action === 'QUERY' || action === 'SEARCH') && data) {
    const hasTable = data.columns?.length > 0 && data.rows?.length > 0;
    const hasSummary = data.summary && Object.keys(data.summary).length > 0;
    return (
      <div className="space-y-3">
        <p className="text-[13px] text-[#1d1d1f] leading-relaxed">{message}</p>
        {hasSummary && (
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(data.summary).map(([key, val]) => (
              <div key={key} className="bg-[#f8f7ff] border border-[#ece9ff] rounded-lg px-3 py-2">
                <p className="text-[10px] text-[#9c9db7] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                <p className="text-[15px] font-bold text-[#1d1d1f]">{typeof val === 'number' ? val.toLocaleString() : val}</p>
              </div>
            ))}
          </div>
        )}
        {hasTable && (
          <div className="border border-[#e5e5ea] rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[300px]">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#f5f5f7]">
                    {data.columns.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold text-[#9c9db7] uppercase tracking-wider text-[10px] whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-[#f0f0f0] hover:bg-[#fafafe]">
                      {(Array.isArray(row) ? row : [row]).map((c, ci) => <td key={ci} className="px-3 py-1.5 whitespace-nowrap text-[#1d1d1f]">{String(c)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {(hasTable || hasSummary) && <ExportDropdown data={data} text={message} />}
      </div>
    );
  }

  if (action === 'ERROR') {
    return (
      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
        <Warning size={15} className="text-red-500 mt-0.5 flex-shrink-0" weight="fill" />
        <p className="text-[13px] text-red-600 leading-relaxed">{message}</p>
      </div>
    );
  }

  // RESPOND or fallback — formatted text response
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] text-[#1d1d1f] leading-[1.7] whitespace-pre-wrap">{message}</p>
      {message.length > 150 && (
        <div className="flex items-center gap-1 pt-1">
          <CopyButton text={message} />
          <ExportDropdown text={message} />
        </div>
      )}
    </div>
  );
}

/* ── Autocomplete Dropdown ─── */
function AutocompleteDropdown({ suggestions, selectedIdx, triggerConfig, onSelect }) {
  if (!suggestions?.length) return null;
  const Icon = triggerConfig.icon;
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-[#e5e5ea] rounded-xl shadow-xl max-h-52 overflow-y-auto z-50" data-testid="autocomplete-dropdown">
      <div className="px-3 py-1.5 border-b border-[#f0f0f0] bg-[#fafafe]">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: triggerConfig.color }}>{triggerConfig.label}</span>
      </div>
      {suggestions.map((item, i) => (
        <button key={i} onClick={() => onSelect(item)} className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left ${i === selectedIdx ? 'bg-[#f8f7ff]' : 'hover:bg-[#fafafe]'}`} data-testid={`autocomplete-item-${i}`}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: triggerConfig.color + '14' }}>
            <Icon size={13} style={{ color: triggerConfig.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-[#1d1d1f] truncate">{item.label}</p>
            {item.sub && <p className="text-[11px] text-[#9c9db7] truncate">{item.sub}</p>}
          </div>
          {i === selectedIdx && <span className="text-[10px] text-[#9c9db7] flex-shrink-0">Enter</span>}
        </button>
      ))}
    </div>
  );
}

/* ── Typing Indicator ─── */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="w-7 h-7 rounded-full bg-[#7d64ff] flex items-center justify-center flex-shrink-0">
        <Robot size={14} weight="fill" className="text-white" />
      </div>
      <div className="flex items-center gap-1.5 bg-[#f5f5f7] rounded-2xl px-4 py-2.5">
        <span className="w-2 h-2 bg-[#9c9db7] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-[#9c9db7] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-[#9c9db7] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN: AI ASSISTANT DRAWER (50% WIDTH)
   ══════════════════════════════════════════════════════ */
export default function AiAssistant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const [acState, setAcState] = useState({ show: false, trigger: null, triggerIndex: -1, suggestions: [], selectedIdx: 0 });
  const debounceRef = useRef(null);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chatHistory, isLoading]);

  // Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(p => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => (textareaRef.current || inputRef.current)?.focus(), 150);
      if (!sessionId) initSession();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const initSession = async () => {
    try {
      const res = await axios.post(`${API}/api/ai/session`, {}, { withCredentials: true });
      setSessionId(res.data.session_id);
    } catch { setSessionId(`ai_local_${Date.now()}`); }
  };

  // Voice
  const toggleVoice = useCallback(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) { toast.error('Voice not supported in this browser'); return; }
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false; rec.interimResults = false; rec.lang = 'en-US';
    rec.onresult = (e) => { setQuery(p => p + e.results[0][0].transcript); setIsListening(false); };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec; rec.start(); setIsListening(true);
  }, [isListening]);

  // Send
  const sendCommand = async (text) => {
    const cmd = text || query.trim();
    if (!cmd || isLoading) return;
    setQuery(''); setAcState(s => ({ ...s, show: false }));
    setChatHistory(prev => [...prev, { role: 'user', content: cmd }]);
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/api/ai/command`, { command: cmd, session_id: sessionId }, { withCredentials: true });
      setChatHistory(prev => [...prev, { role: 'assistant', data: res.data }]);
    } catch {
      setChatHistory(prev => [...prev, { role: 'assistant', data: { action: 'ERROR', message: 'Something went wrong. Please try again.' } }]);
    } finally { setIsLoading(false); }
  };

  const handleNavigate = (path) => { setOpen(false); navigate(path); };

  const clearChat = async () => {
    try { if (sessionId) await axios.delete(`${API}/api/ai/session/${sessionId}`, { withCredentials: true }); } catch {}
    setChatHistory([]); setSessionId(null); initSession();
  };

  // Autocomplete
  const handleInputChange = (val) => {
    setQuery(val);
    let foundTrigger = null, foundIndex = -1;
    for (const char of Object.keys(TRIGGERS)) {
      const idx = val.lastIndexOf(char);
      if (idx >= 0 && idx > foundIndex && (idx === 0 || val[idx - 1] === ' ' || val[idx - 1] === '\n')) {
        const after = val.slice(idx + 1);
        if (!after.includes(' ') && !after.includes('\n')) { foundTrigger = char; foundIndex = idx; }
      }
    }
    if (foundTrigger) {
      const searchTerm = val.slice(foundIndex + 1);
      setAcState(s => ({ ...s, show: true, trigger: foundTrigger, triggerIndex: foundIndex, selectedIdx: 0 }));
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchAutocomplete(foundTrigger, searchTerm), 200);
    } else {
      setAcState(s => ({ ...s, show: false, suggestions: [], trigger: null }));
    }
  };

  const fetchAutocomplete = async (trigger, searchTerm) => {
    try {
      const res = await axios.post(`${API}/api/ai/autocomplete`, { trigger, query: searchTerm }, { withCredentials: true });
      setAcState(s => ({ ...s, suggestions: res.data.suggestions || [] }));
    } catch { setAcState(s => ({ ...s, suggestions: [] })); }
  };

  const selectAutocomplete = (item) => {
    const before = query.slice(0, acState.triggerIndex);
    setQuery(`${before}${acState.trigger}${item.value} `);
    setAcState({ show: false, trigger: null, triggerIndex: -1, suggestions: [], selectedIdx: 0 });
    (textareaRef.current || inputRef.current)?.focus();
  };

  const handleKeyDown = (e) => {
    if (acState.show && acState.suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcState(s => ({ ...s, selectedIdx: Math.min(s.selectedIdx + 1, s.suggestions.length - 1) })); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAcState(s => ({ ...s, selectedIdx: Math.max(s.selectedIdx - 1, 0) })); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectAutocomplete(acState.suggestions[acState.selectedIdx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setAcState(s => ({ ...s, show: false })); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !acState.show) { e.preventDefault(); sendCommand(); }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    handleInputChange(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const triggerConfig = acState.trigger ? TRIGGERS[acState.trigger] : null;

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 group flex items-center gap-2 h-12 pl-4 pr-3 rounded-full bg-[#7d64ff] text-white shadow-lg hover:bg-[#6a54e0] hover:shadow-xl transition-all z-40" title="AI Assistant (Cmd+K)" data-testid="ai-assistant-toggle">
        <Robot size={20} weight="fill" />
        <span className="text-[13px] font-semibold hidden sm:inline">Ask AI</span>
        <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-mono"><Command size={10} />K</kbd>
      </button>

      {/* Half-width Drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:w-[50vw] sm:max-w-[50vw] p-0 flex flex-col border-l border-[#e5e5ea] [&>button]:hidden">
          <SheetTitle className="sr-only">Vinculum AI Assistant</SheetTitle>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e5e5ea] bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#7d64ff] flex items-center justify-center">
                <Robot size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-[#1d1d1f]">Vinculum AI</h3>
                <p className="text-[11px] text-[#9c9db7]">Ask anything about your business data</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {chatHistory.length > 0 && (
                <button onClick={clearChat} className="p-2 rounded-lg hover:bg-[#f5f5f7] text-[#9c9db7] hover:text-[#5a5c87] transition-colors" title="New conversation" data-testid="ai-clear-btn">
                  <Trash size={16} />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-[#f5f5f7] text-[#9c9db7] hover:text-[#5a5c87] transition-colors" data-testid="ai-close-btn">
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Chat area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="ai-messages">
            {chatHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-6 py-8">
                <div className="w-16 h-16 rounded-2xl bg-[#f2f0ff] flex items-center justify-center mb-4">
                  <Robot size={32} className="text-[#7d64ff]" weight="duotone" />
                </div>
                <h2 className="text-lg font-bold text-[#1d1d1f] mb-1">How can I help you?</h2>
                <p className="text-[13px] text-[#9c9db7] text-center mb-6 max-w-sm">
                  Ask any question about your orders, products, returns, revenue, or anything else. I can query your data, show tables, and export results.
                </p>

                {/* Starter prompts */}
                <div className="w-full max-w-lg grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {STARTERS.map((s, i) => (
                    <button key={i} onClick={() => sendCommand(s.text)} className="flex items-center gap-2.5 px-4 py-3 bg-white border border-[#e5e5ea] rounded-xl hover:border-[#7d64ff]/40 hover:bg-[#fafafe] transition-all text-left group" data-testid={`ai-suggestion-${i}`}>
                      <s.icon size={16} className="text-[#9c9db7] group-hover:text-[#7d64ff] flex-shrink-0" />
                      <span className="text-[13px] text-[#3c3c64] group-hover:text-[#1d1d1f]">{s.text}</span>
                    </button>
                  ))}
                </div>

                {/* Entity trigger reference */}
                <div className="w-full max-w-lg bg-[#fafafe] border border-[#f0f0f0] rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-[#7d64ff] uppercase tracking-wider mb-2">Entity shortcuts</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                    {Object.entries(TRIGGERS).map(([char, conf]) => (
                      <div key={char} className="flex items-center gap-1.5">
                        <kbd className="inline-flex items-center justify-center w-5 h-5 rounded text-[11px] font-mono font-bold border border-[#e5e5ea] bg-white" style={{ color: conf.color }}>{char}</kbd>
                        <span className="text-[11px] text-[#5a5c87]">{conf.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-5 py-5 space-y-1">
                {chatHistory.map((msg, idx) => (
                  <div key={idx}>
                    {msg.role === 'user' ? (
                      /* User message */
                      <div className="flex items-start gap-3 justify-end mb-4">
                        <div className="max-w-[80%] px-4 py-3 text-[13px] bg-[#7d64ff] text-white rounded-2xl rounded-br-md leading-relaxed">
                          {msg.content}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#e5e5ea] flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-[#5a5c87]">
                          U
                        </div>
                      </div>
                    ) : (
                      /* Assistant message */
                      <div className="flex items-start gap-3 mb-5">
                        <div className="w-7 h-7 rounded-full bg-[#7d64ff] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Robot size={14} weight="fill" className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <AIResponseDisplay response={msg.data} onNavigate={handleNavigate} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && <TypingIndicator />}
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="px-5 py-4 border-t border-[#e5e5ea] bg-white flex-shrink-0">
            <div className="relative">
              {acState.show && triggerConfig && acState.suggestions?.length > 0 && (
                <AutocompleteDropdown suggestions={acState.suggestions} selectedIdx={acState.selectedIdx} triggerConfig={triggerConfig} onSelect={selectAutocomplete} />
              )}
              <div className="flex items-end gap-2 bg-[#f5f5f7] border border-[#e5e5ea] rounded-2xl px-4 py-2 focus-within:border-[#7d64ff] focus-within:ring-2 focus-within:ring-[#7d64ff]/10 transition-all">
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about your business..."
                  className="flex-1 bg-transparent text-[14px] text-[#1d1d1f] placeholder-[#c7c7cc] outline-none resize-none leading-relaxed py-1"
                  rows={1}
                  style={{ maxHeight: '120px' }}
                  disabled={isLoading}
                  data-testid="ai-input"
                />
                <div className="flex items-center gap-1 flex-shrink-0 pb-0.5">
                  <button onClick={toggleVoice} className={`p-1.5 rounded-lg transition-colors ${isListening ? 'bg-red-100 text-red-500' : 'hover:bg-[#e5e5ea] text-[#9c9db7]'}`} title={isListening ? 'Stop' : 'Voice input'} data-testid="voice-input-btn">
                    {isListening ? <Stop size={16} weight="fill" /> : <Microphone size={16} />}
                  </button>
                  <button onClick={() => sendCommand()} disabled={isLoading || !query.trim()} className="p-2 bg-[#7d64ff] text-white rounded-xl hover:bg-[#6a54e0] disabled:opacity-30 disabled:hover:bg-[#7d64ff] transition-colors" data-testid="ai-send-btn">
                    <PaperPlaneTilt size={16} weight="fill" />
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-[#c7c7cc] mt-1.5 text-center">
                Use <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono">@</kbd> <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono">#</kbd> <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono">$</kbd> <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono">!</kbd> <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono">%</kbd> for entity search &middot; <kbd className="px-1 py-0.5 bg-white border border-[#e5e5ea] rounded text-[9px] font-mono"><Command size={8} className="inline" />K</kbd> to toggle
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
